/**
 * One shared, cached subscription to the portal-visible ("live") clients.
 *
 * Before this every page (Advisory / Paraplanner portals, My Assigned Tasks,
 * Calendar, Send Envelope) opened its own listener on the WHOLE clients
 * collection — ~5,600 full docs / ~34 MB, 88% of them unreleased CSV-merge
 * drafts no portal shows — and re-downloaded it on every visit.
 *
 * Now:
 *   - it reads the slim list rows (clientListRows, src/utils/clientListRow.js):
 *     only the fields the lists use, ~1.3 MB instead of ~27 MB of full live
 *     client docs (Oct 2026). Until the server mirror has written rows (or the
 *     rules allow them) it falls back to `clients where listBucket == 'live'`;
 *   - all subscribers share one Firestore listener and one clients array;
 *   - the listener starts at sign-in (prefetchLiveClients) and lingers for
 *     LINGER_MS after the last subscriber leaves, so navigating away and back
 *     is instant (no re-download);
 *   - the cache is dropped when the signed-in user changes.
 *
 * The delivered objects are list rows, not full client docs: a screen that
 * needs other fields reads the client doc (getClientRecord / selectClient).
 * The CSV Merge portal still uses subscribeToAllClients — it needs the drafts.
 * Treat the delivered array and client objects as read-only: they are shared.
 */
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { CLIENT_LIST_BUCKET_FIELD, CLIENT_LIST_BUCKETS } from '../utils/clientListBucket';
import { CLIENT_LIST_ROWS_COLLECTION, markClientListRow } from '../utils/clientListRow';
import {
  applyLiveClientPatches,
  clearLiveClientPatches,
  setLiveClientPatchListener,
  settleLiveClientPatch,
} from './liveClientPatches';

export {
  addLiveClient,
  dropLiveClientPatch,
  patchLiveClient,
  removeLiveClient,
} from './liveClientPatches';

const CLIENTS_COLLECTION = 'clients';
const LINGER_MS = 10 * 60 * 1000;
const SOURCE_ROWS = 'rows';
const SOURCE_CLIENTS = 'clients';

const listeners = new Set();
const clientsById = new Map();
const createdAtMsById = new Map();
let unsubscribeSnapshot = null;
let lingerTimer = null;
let latest = null; // { success: true, clients } | { success: false, error }
let authUid;
let prefetchUid = null;
let source = SOURCE_ROWS;

// createdAt is a Firestore Timestamp on some docs and an ISO string on others.
function createdAtMs(client) {
  const value = client?.createdAt;
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

// Changes this browser just saved (liveClientPatches.js) show over the rows.
function publish() {
  const clients = Array.from(applyLiveClientPatches(new Map(clientsById)).values());
  const createdMs = (client) => (createdAtMsById.has(client.id) ? createdAtMsById.get(client.id) : createdAtMs(client));
  clients.sort((a, b) => createdMs(b) - createdMs(a));
  latest = { success: true, clients };
  emit();
}

function emit() {
  listeners.forEach((listener) => {
    try {
      listener(latest);
    } catch (error) {
      console.error('liveClientsStore listener failed:', error);
    }
  });
}

function clearLinger() {
  if (lingerTimer) {
    clearTimeout(lingerTimer);
    lingerTimer = null;
  }
}

function stopListening() {
  if (unsubscribeSnapshot) {
    unsubscribeSnapshot();
    unsubscribeSnapshot = null;
  }
  clientsById.clear();
  createdAtMsById.clear();
}

function stop() {
  clearLinger();
  stopListening();
  clearLiveClientPatches();
  latest = null;
  source = SOURCE_ROWS;
}

function lingerIfUnused() {
  if (listeners.size === 0 && unsubscribeSnapshot && !lingerTimer) {
    lingerTimer = setTimeout(stop, LINGER_MS);
  }
}

// The server mirror has not written rows yet (or the rules do not allow them):
// read the full live client docs for the rest of this sign-in.
function fallBackToClientDocs(reason) {
  console.info(`[liveClients] reading full client docs (${reason})`);
  stopListening();
  source = SOURCE_CLIENTS;
  start();
}

function start() {
  if (unsubscribeSnapshot) return;
  const listenSource = source;
  const q = listenSource === SOURCE_ROWS
    ? collection(db, CLIENT_LIST_ROWS_COLLECTION)
    : query(
      collection(db, CLIENTS_COLLECTION),
      where(CLIENT_LIST_BUCKET_FIELD, '==', CLIENT_LIST_BUCKETS.LIVE),
    );
  unsubscribeSnapshot = onSnapshot(
    q,
    (snapshot) => {
      if (listenSource === SOURCE_ROWS && snapshot.empty && clientsById.size === 0 && !snapshot.metadata.fromCache) {
        fallBackToClientDocs('no list rows');
        return;
      }
      // Only changed docs get new objects, so unchanged rows keep their identity.
      snapshot.docChanges().forEach((change) => {
        const { id } = change.doc;
        if (change.type === 'removed') {
          clientsById.delete(id);
          createdAtMsById.delete(id);
          settleLiveClientPatch(id, null);
        } else {
          const client = { id, ...change.doc.data() };
          if (listenSource === SOURCE_ROWS) markClientListRow(client);
          clientsById.set(id, client);
          createdAtMsById.set(id, createdAtMs(client));
          settleLiveClientPatch(id, client);
        }
      });
      publish();
    },
    (error) => {
      if (listenSource === SOURCE_ROWS && error?.code === 'permission-denied' && auth.currentUser) {
        // Firestore ended the listener; the rows rule is not deployed yet.
        unsubscribeSnapshot = null;
        fallBackToClientDocs('list rows not readable');
        return;
      }
      console.error('Error in live clients subscription:', error);
      // Firestore ends a listener after an error; the next subscribe starts a fresh one.
      unsubscribeSnapshot = null;
      clientsById.clear();
      createdAtMsById.clear();
      latest = { success: false, error: error.message };
      emit();
      latest = null;
    },
  );
}

setLiveClientPatchListener(() => {
  if (latest?.success) publish();
});

onAuthStateChanged(auth, (user) => {
  const uid = user?.uid || null;
  if (authUid !== undefined && uid !== authUid) {
    // Signed out or switched account: never serve the previous user's clients.
    const prefetchedForUid = prefetchUid;
    stop();
    if (uid && (listeners.size > 0 || prefetchedForUid === uid)) {
      start();
      lingerIfUnused();
    }
  }
  if (!uid) prefetchUid = null;
  authUid = uid;
});

/**
 * Start downloading the live clients as soon as someone is signed in, so the
 * home portal does not wait for the startup checks first. Unused, the listener
 * stops after LINGER_MS.
 */
export function prefetchLiveClients() {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  prefetchUid = uid;
  start();
  lingerIfUnused();
}

/**
 * The last delivered result (`{ success, clients }`), or null before the first
 * snapshot — lets a freshly mounted page render the list on its first frame.
 */
export function getCachedLiveClients() {
  return latest;
}

/**
 * Same callback contract as subscribeToAllClients: `({ success, clients, error })`.
 * Delivers the cached list straight away when one is loaded.
 * @returns {() => void} unsubscribe
 */
export function subscribeToLiveClients(callback) {
  listeners.add(callback);
  clearLinger();
  start();
  if (latest) {
    const cached = latest;
    queueMicrotask(() => {
      if (listeners.has(callback)) callback(cached);
    });
  }
  return () => {
    listeners.delete(callback);
    lingerIfUnused();
  };
}
